import { useMcpToolsMetadataQuery } from "@qw/graphql";
import { MCPPlatform } from "@qw/qw-common";
import { createContext, type FC, type PropsWithChildren, useContext, useMemo } from "react";

/**
 * Tool name → the human label the transcript shows for it (the tool's short description, its full
 * description, or nothing when the server has neither).
 */
export type ToolDescriptionLookup = ReadonlyMap<string, string>;

/**
 * Stable empty lookup for trees rendered outside {@link ChatToolMetadataProvider}. Every consumer
 * falls back to the raw tool name, which is what an unresolved query produced before this provider
 * existed — so a detached tool invocation renders exactly as it used to.
 */
const NO_TOOL_DESCRIPTIONS: ToolDescriptionLookup = new Map();

const ChatToolMetadataContext = createContext<ToolDescriptionLookup>(NO_TOOL_DESCRIPTIONS);

/**
 * Fetches the MCP tool metadata ONCE per chat surface and hands the transcript a stable lookup map.
 *
 * Every rendered tool invocation used to call `useMcpToolsMetadataQuery` itself and then scan the
 * returned list for its own tool. Apollo served those from cache, but each invocation still carried
 * its own watched query and re-scanned the whole list on every render — work that scaled with
 * (historical invocations × tools) and repeated on each streamed token. One query at the surface
 * keeps that cost at one fetch plus one map build.
 *
 * The map's identity only changes when the metadata itself does, so it does not participate in the
 * per-token churn of the chat context.
 */
export const ChatToolMetadataProvider: FC<PropsWithChildren> = ({ children }) => {
  // Scoped to INBOX, matching the per-invocation query this replaced. The surface's own `platform`
  // is deliberately not used: no tool is tagged WEB without also being tagged INBOX, so the INBOX set
  // is a superset of what a WEB surface can invoke and every surface resolves the same labels from
  // it. Keying on `platform` instead would split this into one cached query per surface for no
  // difference in what renders.
  const { data } = useMcpToolsMetadataQuery({
    variables: { input: { platforms: [MCPPlatform.INBOX] } },
  });

  const toolDescriptions = useMemo<ToolDescriptionLookup>(() => {
    const descriptions = new Map<string, string>();

    for (const tool of data?.mcpToolsMetadata ?? []) {
      const description = tool.shortDescription || tool.description;

      if (description) {
        descriptions.set(tool.name, description);
      }
    }

    return descriptions;
  }, [data?.mcpToolsMetadata]);

  return <ChatToolMetadataContext.Provider value={toolDescriptions}>{children}</ChatToolMetadataContext.Provider>;
};

/**
 * The label for one tool, from the surface-wide lookup. Falls back to the tool's own name when the
 * server has no description for it or the metadata hasn't loaded yet.
 */
export const useToolDescription = (toolName: string): string =>
  useContext(ChatToolMetadataContext).get(toolName) ?? toolName;
